import React from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Button, 
  Container,
  VStack,
  Image,
  SimpleGrid,
  Flex,
  useColorModeValue,
  Icon,
  Stack
} from '@chakra-ui/react';
import { FaCoins, FaGift, FaMoneyBillWave, FaChartLine } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';

const HomePage: React.FC = () => {
  const { walletAddress } = useWallet();
  const cardBg = useColorModeValue('white', 'gray.700');

  return (
    <Container maxW="container.xl">
      {/* Hero section */}
      <Box 
        py={{ base: 10, md: 20 }}
        textAlign="center"
      >
        <Heading
          fontWeight={700}
          fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
          lineHeight={'110%'}
          mb={6}
        >
          Win SOL with <Text as={'span'} color={'brand.500'}>BlindBox</Text>
        </Heading>
        
        <Text 
          color={'gray.500'} 
          maxW={'3xl'} 
          fontSize={{ base: 'md', md: 'xl' }}
          mx="auto"
          mb={10}
        >
          Open mystery boxes for a chance to win up to 5 SOL! Provide liquidity and earn rewards from the pool's growth.
        </Text>

        <Stack
          direction={{ base: 'column', sm: 'row' }}
          mb={10}
          spacing={4}
          justify="center"
        >
          <Button
            as={RouterLink}
            to="/blindbox"
            rounded="full"
            size="lg"
            fontWeight="bold"
            px={6}
            colorScheme="brand"
            bg="brand.500"
            _hover={{ bg: 'brand.600' }}
          >
            Try Your Luck!
          </Button>
          <Button
            as={RouterLink}
            to="/liquidity"
            rounded="full"
            size="lg"
            fontWeight="bold"
            px={6}
            colorScheme="brand"
            variant="outline"
          >
            Provide Liquidity
          </Button>
        </Stack>

        <Flex
          justify="center"
          align="center"
          mb={{ base: 10, md: 20 }}
        >
          <Image
            src="https://via.placeholder.com/800x400?text=BlindBox+Game"
            alt="BlindBox"
            borderRadius="lg"
            shadow="2xl"
            maxW={{ base: '100%', md: '80%' }}
          />
        </Flex>
      </Box>

      {/* Features */}
      <Box py={10}>
        <Heading
          fontSize={{ base: '2xl', md: '3xl' }}
          textAlign="center"
          mb={12}
        >
          How It Works
        </Heading>
        
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={10}>
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="lg"
            boxShadow="sm"
            textAlign="center"
            spacing={4}
          >
            <Icon as={FaCoins} w={10} h={10} color="brand.500" />
            <Heading size="md">1. Connect Wallet</Heading>
            <Text color="gray.500">Connect your wallet and add SOL to get started.</Text>
          </VStack>
          
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="lg"
            boxShadow="sm"
            textAlign="center"
            spacing={4}
          >
            <Icon as={FaGift} w={10} h={10} color="brand.500" />
            <Heading size="md">2. Buy BlindBox</Heading>
            <Text color="gray.500">Pay 1 SOL to open a mystery box with random rewards.</Text>
          </VStack>
          
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="lg"
            boxShadow="sm"
            textAlign="center"
            spacing={4}
          >
            <Icon as={FaMoneyBillWave} w={10} h={10} color="brand.500" />
            <Heading size="md">3. Add Liquidity</Heading>
            <Text color="gray.500">Provide liquidity to the pool and earn rewards.</Text>
          </VStack>
          
          <VStack
            bg={cardBg}
            p={6}
            borderRadius="lg" 
            boxShadow="sm"
            textAlign="center"
            spacing={4}
          >
            <Icon as={FaChartLine} w={10} h={10} color="brand.500" />
            <Heading size="md">4. Earn Rewards</Heading>
            <Text color="gray.500">Claim your share of 50% of the pool's growth.</Text>
          </VStack>
        </SimpleGrid>
      </Box>

      {/* CTA */}
      <Box py={16} textAlign="center">
        <Heading mb={6}>Ready to Try Your Luck?</Heading>
        <Text color="gray.500" mb={8} maxW="2xl" mx="auto">
          Each BlindBox costs just 1 SOL and gives you a chance to win up to 5 SOL instantly!
        </Text>
        
        <Button
          as={RouterLink}
          to={walletAddress ? '/blindbox' : '/dashboard'}
          size="lg"
          colorScheme="brand"
          rounded="full"
          px={8}
        >
          {walletAddress ? 'Open a BlindBox' : 'Get Started'}
        </Button>
      </Box>
    </Container>
  );
};

export default HomePage; 